/**
 * Grade read-across: the suggestion engine (PD-1293).
 *
 * "Our Grade 11 is their Level 6." A transitioning client keeps its own grade chart
 * while the business moves onto RoleMapper levels. Each grade is placed at the level
 * its EVALUATED roles actually score at, so the two can be read across in either
 * language. The link is transitional by design — it is meant to fall away.
 *
 * Pure functions over a role list, like anchorSetup.js, so both the alignment grid
 * and the mapping panel compute the same numbers from the same input and neither can
 * drift from the other.
 *
 * A SCHEME IS A TEMPLATE ATTRIBUTE (`tpa_id`) — every tmpl_part_attr marked
 * levelling_framework_attribute = 'LEGACY GRADE'. Two templates each marking their
 * own attribute are two grade charts lined up against the one ladder.
 *
 * TWO DELIBERATE DIVERGENCES FROM THE PROTOTYPE, which had a fixed demo register:
 *
 *  1. No grade PARSING. The prototype recognises `CB07` / `N3` by regex and calls
 *     everything else "messy — clean it first". Real grades are free text and no
 *     regex can say which spellings are legitimate, so every non-blank value is a
 *     grade here. What IS honest to do is normalise for grouping (trim, collapse
 *     inner whitespace, case-fold) so "g11", "G11 " and "G11" are one grade rather
 *     than three one-role grades that each read as thin evidence. The most common
 *     original spelling is what gets displayed.
 *
 *  2. Levels, not bands. The prototype maps a grade to a global band number; here it
 *     maps to an `lf_level_id`, ranked by `lf_level.position`, because that is what
 *     the real ladder is keyed on.
 */

/** Grades below this many evaluated roles are thin evidence — the anchor floor. */
export { ANCHOR_FLOOR, LARGE_ORG_ROLES } from './anchorSetup';

/**
 * Grouping key for a grade. Case and stray whitespace are spelling, not meaning:
 * merging them is the difference between one grade with eight roles behind it and
 * three grades that each fall below the floor.
 */
export function gradeKey(grade) {
  return String(grade == null ? '' : grade).trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Scheme + grade, the identity a link is stored against. */
export function linkKey(tpaId, grade) {
  return `${Number(tpaId)}:${gradeKey(grade)}`;
}

/**
 * Sort grades the way a person reads a grade chart: by any number in them, then
 * alphabetically. "G2" before "G10" — a plain string sort puts G10 first, which makes
 * a grade chart look shuffled.
 */
export function compareGrades(a, b) {
  const na = /(\d+)/.exec(a || '');
  const nb = /(\d+)/.exec(b || '');
  const pa = (a || '').replace(/\d+/, '');
  const pb = (b || '').replace(/\d+/, '');
  if (na && nb && pa === pb) return Number(na[1]) - Number(nb[1]);
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });
}

/** The roles that count as evidence: a level from an evaluation, and a grade. */
function isEvidence(role) {
  return !!(role && role.lf_level_id && gradeKey(role.legacyGrade));
}

/** Most frequent key of a {value: count} tally, ties broken deterministically. */
function modalOf(tally) {
  const keys = Object.keys(tally);
  if (!keys.length) return null;
  return keys.sort((x, y) => tally[y] - tally[x] || String(x).localeCompare(String(y)))[0];
}
function totalOf(tally) {
  return Object.values(tally).reduce((a, b) => a + b, 0);
}

/**
 * Evidence per grade, across the whole register.
 *
 * @param {Array}  roles  the frameworkInUse payload (legacyGrade, gradeTpaId, lf_level_id, levelPosition, family, familyGroup)
 * @param {Object} opts   { floor } — evaluated roles a grade needs before its link is dependable
 * @returns {Object} linkKey -> {
 *   tpa_id, grade, total, levels {id:count}, levelPositions {id:position},
 *   familyGroups { group: { levelId: count } }, lf_level_id (the suggestion),
 *   agreement, divergent, divergentGroups [], state 'thin'|'divergent'|'ok'
 * }
 */
export function gradeSuggestions(roles, opts) {
  const floor = (opts && opts.floor) || 2;
  const acc = {};

  for (const r of Array.isArray(roles) ? roles : []) {
    if (!isEvidence(r)) continue;
    const key = linkKey(r.gradeTpaId, r.legacyGrade);
    const a = acc[key] || (acc[key] = {
      key,
      tpa_id: Number(r.gradeTpaId),
      spellings: {},
      total: 0,
      levels: {},
      levelPositions: {},
      familyGroups: {},
      families: {},
    });
    const levelId = Number(r.lf_level_id);
    a.total++;
    a.levels[levelId] = (a.levels[levelId] || 0) + 1;
    if (r.levelPosition != null) a.levelPositions[levelId] = Number(r.levelPosition);
    // Keep the original spellings so the chart shows the client's own, not a shouted one.
    const spelling = String(r.legacyGrade).trim().replace(/\s+/g, ' ');
    a.spellings[spelling] = (a.spellings[spelling] || 0) + 1;
    if (r.familyGroup) {
      const g = a.familyGroups[r.familyGroup] || (a.familyGroups[r.familyGroup] = {});
      g[levelId] = (g[levelId] || 0) + 1;
    }
    if (r.family) {
      const f = a.families[r.family] || (a.families[r.family] = {});
      f[levelId] = (f[levelId] || 0) + 1;
    }
  }

  const out = {};
  for (const key of Object.keys(acc)) {
    const a = acc[key];
    /* Most-used level wins. A genuine tie resolves to the MORE SENIOR level — the
       prototype's rule, and the safer default when the link may pre-fill a pay-bearing
       decision: under-levelling a grade is the more costly mistake to discover late. */
    const ranked = Object.keys(a.levels)
      .sort((x, y) => a.levels[y] - a.levels[x] || (a.levelPositions[y] || 0) - (a.levelPositions[x] || 0));
    const top = Number(ranked[0]);
    const second = ranked[1] != null ? Number(ranked[1]) : null;
    /* Divergent when a competing level has real weight behind it, not just one
       outlier: at least two roles AND 30% of the evidence. Below that a single
       differently-levelled role would split every grade in the register. */
    const divergent = second != null && a.levels[second] >= Math.max(2, a.total * 0.3);

    const divergentGroups = [];
    for (const group of Object.keys(a.familyGroups)) {
      const tally = a.familyGroups[group];
      // One role in a family group is an anecdote, not a disagreement.
      if (totalOf(tally) < 2) continue;
      const there = Number(modalOf(tally));
      if (there !== top) divergentGroups.push({ familyGroup: group, lf_level_id: there });
    }

    out[key] = {
      key,
      tpa_id: a.tpa_id,
      grade: modalOf(a.spellings),
      total: a.total,
      levels: a.levels,
      levelPositions: a.levelPositions,
      familyGroups: a.familyGroups,
      families: a.families,
      lf_level_id: top,
      agreement: a.levels[top] / a.total,
      divergent,
      divergentGroups,
      floor,
      /* Thin beats divergent: with too little evidence the disagreement is not yet
         meaningful, and telling someone to resolve a split they cannot see is worse
         than telling them to evaluate more roles. */
      state: a.total < floor ? 'thin' : divergent ? 'divergent' : 'ok',
    };
  }
  return out;
}

/**
 * The draft map: suggestions, with the admin's overrides applied over the top.
 *
 * An override whose value is null means DELIBERATELY UNMAPPED — this grade must never
 * pre-fill — which is not the same as having no override, where the suggestion stands.
 * That is why the override list is consulted by key presence, not by truthiness.
 *
 * @returns {Object} linkKey -> lf_level_id | null
 */
export function resolveMap(suggestions, overrides) {
  const ov = {};
  for (const o of Array.isArray(overrides) ? overrides : []) {
    ov[linkKey(o.tpa_id, o.grade)] = o.lf_level_id == null ? null : Number(o.lf_level_id);
  }
  const map = {};
  for (const key of Object.keys(suggestions || {})) {
    map[key] = key in ov ? ov[key] : suggestions[key].lf_level_id;
  }
  // An override can name a grade no longer present in the evidence — keep it, so the
  // decision survives the roles that produced it being re-levelled or archived.
  for (const key of Object.keys(ov)) {
    if (!(key in map)) map[key] = ov[key];
  }
  return map;
}

/** Was this link moved by hand, away from where its evidence points? */
export function isOverridden(suggestion, map) {
  if (!suggestion) return false;
  const to = map[suggestion.key];
  return to !== undefined && to !== suggestion.lf_level_id;
}

/**
 * Where the same grade sits at different levels depending on the function.
 *
 * Stated explicitly rather than left to be inferred from the grid: it is the finding
 * the alignment view exists to surface, and the reason a grade routes to review.
 */
export function gradeSplits(suggestions, map, familyGroups) {
  const wanted = Array.isArray(familyGroups) && familyGroups.length ? new Set(familyGroups) : null;
  const out = [];
  for (const s of Object.values(suggestions || {})) {
    const home = map[s.key];
    if (home == null) continue;
    for (const d of s.divergentGroups) {
      if (wanted && !wanted.has(d.familyGroup)) continue;
      if (d.lf_level_id === home) continue;
      out.push({ ...d, key: s.key, tpa_id: s.tpa_id, grade: s.grade, home });
    }
  }
  return out;
}

/** The family groups whose evidence disagrees with the company-wide link. */
export function divergentFamilyGroups(suggestions, map) {
  const seen = new Set();
  for (const s of gradeSplits(suggestions, map, null)) seen.add(s.familyGroup);
  return [...seen].sort();
}

/**
 * The map as it would be published: every link with a level behind it.
 *
 * Deliberately-unmapped grades are dropped rather than sent as nulls — a published
 * version records what the read-across IS, and "no link" is the absence of an entry.
 */
export function publishableEntries(map, suggestions) {
  const out = [];
  for (const key of Object.keys(map || {})) {
    if (map[key] == null) continue;
    const s = (suggestions || {})[key];
    const [tpaId, fallbackGrade] = key.split(':');
    out.push({
      tpa_id: Number(tpaId),
      grade: s ? s.grade : fallbackGrade,
      lf_level_id: Number(map[key]),
    });
  }
  return out;
}

/**
 * How a published version differs from the draft in front of the admin — what the
 * "unpublished changes" count means.
 */
export function unpublishedChanges(map, published) {
  const pub = {};
  for (const e of (published && published.entries) || []) pub[linkKey(e.tpa_id, e.grade)] = e.lf_level_id;
  const keys = new Set([...Object.keys(map || {}), ...Object.keys(pub)]);
  let n = 0;
  for (const k of keys) {
    const a = map && map[k] != null ? map[k] : null;
    const b = pub[k] != null ? pub[k] : null;
    if (a !== b) n++;
  }
  return n;
}
